/**
 * TourRouteChips Component
 * 
 * Displays tour route as pill-shaped badges.
 * Uses normalized data from QuotationFormatter.getRouteVisualData()
 * 
 * Usage:
 *   const formatter = new QuotationFormatter(quotation, agency);
 *   const routeData = formatter.getRouteVisualData();
 *   
 *   <TourRouteChips
 *     data={routeData}
 *     packageType={quotation.route_type}
 *     variant="detail"
 *   />
 */

import React from 'react';
import { MapPin, ArrowRight, ArrowDown } from 'lucide-react';
import { COMPONENT_VARIANTS } from './displayConfig';

const TourRouteChips = ({ 
  data = [],
  packageType = 'custom',
  variant = 'detail',
  showTitle = true,
  className = ''
}) => {
  if (!data || data.length === 0) {
    return null;
  }

  const styles = COMPONENT_VARIANTS[variant] || COMPONENT_VARIANTS.detail;

  // Chip colors - uniform white background with border for all pills
  const getChipStyle = (stop) => {
    return 'bg-white border border-gray-300 text-gray-800';
  };

  // Build chip label
  const getChipLabel = (stop) => {
    let label = stop.name;
    
    if (stop.isArrival && stop.nights) {
      label += ` (Arrival/${stop.nights}N)`;
    } else if (stop.isArrival) {
      label += ' (Arrival)';
    } else if (stop.isDeparture) {
      label += ' (Drop)';
    } else if (stop.nights) {
      label += ` (${stop.nights}N)`;
    }
    
    return label;
  };

  return (
    <div 
      className={className}
      data-package-type={packageType}
      data-component="tour-route"
    >
      {showTitle && (
        <h3 className={`font-semibold ${variant === 'preview' ? 'text-sm' : 'text-lg'} mb-3 flex items-center gap-2`}>
          <MapPin className={`${variant === 'preview' ? 'w-4 h-4' : 'w-5 h-5'} text-[var(--navy)]`} />
          Tour Route
        </h3>
      )}

      {/* Mobile (<640px): vertical stack with down-arrows, no dangling arrows on wrap */}
      <div className="flex sm:hidden flex-col items-start gap-1.5">
        {data.map((stop, idx) => (
          <React.Fragment key={idx}>
            <span
              className={`px-3 py-1.5 rounded-full font-medium text-xs ${getChipStyle(stop)}`}
            >
              {getChipLabel(stop)}
            </span>
            {idx < data.length - 1 && (
              <ArrowDown className="w-3.5 h-3.5 text-gray-400 ml-3" />
            )}
          </React.Fragment>
        ))}
      </div>

      {/* Desktop (>=640px): horizontal row with right-arrows */}
      <div className="hidden sm:flex flex-wrap items-center gap-2">
        {data.map((stop, idx) => (
          <span
            key={idx}
            className="inline-flex items-center gap-2"
          >
            <span 
              className={`
                px-3 py-1.5 rounded-full font-medium
                ${variant === 'preview' ? 'text-xs' : 'text-sm'}
                ${getChipStyle(stop)}
              `}
            >
              {getChipLabel(stop)}
            </span>
            
            {/* Arrow separator (except for last item) */}
            {idx < data.length - 1 && (
              <ArrowRight className="w-4 h-4 text-gray-400 shrink-0" />
            )}
          </span>
        ))}
      </div>
    </div>
  );
};

export default TourRouteChips;
